import React, { Component } from 'react';
import { html } from 'lit';
import { AGGRID_LICENCE_KEY, AlertMessagesService, CustomStatusBar, exportStatusHtml, LoaderComponent, MediaOpsService, NoDataComponent, OmniSearchComponent, OmniToolbarComponent, OmniIconComponent, OmniTooltipNextComponent, trackEvent, Utils } from '../../../../shared';
import { AgGridReact } from 'ag-grid-react';
import { defaultColDef, CampaignTableAgGridV1ColumnDefs } from './campaignTableAgGridV1Columns';
import ActionMenuRenderer from './actionMenuRenderer';
import MoreInfoRenderer from './moreInfoRenderer';
import CustomLoadingCellRenderer from './customLoadingCellRenderer';
import {
    AllCommunityModule,
    ModuleRegistry,
    ClientSideRowModelModule,
    ValidationModule,
    NumberEditorModule,
    DateFilterModule,
    NumberFilterModule,
    TextFilterModule,
    CustomFilterModule,
    CsvExportModule,
    RowSelectionModule,
    DateEditorModule
} from "ag-grid-community";
import {
    TreeDataModule,
    ColumnsToolPanelModule,
    FiltersToolPanelModule,
    PivotModule,
    SetFilterModule,
    StatusBarModule,
    ExcelExportModule,
    LicenseManager,
} from "ag-grid-enterprise";

import { FilterCampaign } from './campaign-filter';
import CopyPlacementsDialog from '../CopyPlacements/CopyPlacementsDialog';
// Register the required modules with the Grid
ModuleRegistry.registerModules([
    TreeDataModule,
    AllCommunityModule,
    ValidationModule,
    NumberEditorModule,
    ColumnsToolPanelModule,
    FiltersToolPanelModule,
    PivotModule,
    SetFilterModule,
    ClientSideRowModelModule,
    DateFilterModule,
    NumberFilterModule,
    TextFilterModule,
    StatusBarModule,
    CustomFilterModule,
    CsvExportModule,
    ExcelExportModule,
    RowSelectionModule,
    DateEditorModule
]);

LicenseManager.setLicenseKey(AGGRID_LICENCE_KEY);

/**
 * Renders the planned net media budget with proper currency formatting
 * @param {Object} props - AG-Grid cell renderer props
 * @returns {JSX.Element} Formatted currency display
 */
const PlannedNetMediaRenderer = React.memo((props) => {
    const isLocal = props.context.componentParent.props?.systemParametersData?.data?.isLocal;
    if (isLocal) {
        const displayValue = Utils.getLocalCurrency(props?.data?.local_currency_name ? props?.data?.local_currency_name : 'USD', Number(props?.data?.campaign_total_budget_local || 0))
        return (<span>{displayValue}</span>)
    } else {
        const displayValue = Utils.getLocalCurrency('USD', Number(props?.data?.campaign_total_budget || 0))
        return (<span>{displayValue}</span>)
    }
}, (prevProps, nextProps) => {
    const prevSystemParams = prevProps.context.componentParent.props?.systemParametersData?.data?.isLocal;
    const nextSystemParams = nextProps.context.componentParent.props?.systemParametersData?.data?.isLocal;

    return prevProps?.data?.campaign_total_budget === nextProps?.data?.campaign_total_budget &&
        prevProps?.data?.campaign_total_budget_local === nextProps?.data?.campaign_total_budget_local &&
        prevProps?.data?.local_currency_name === nextProps?.data?.local_currency_name &&
        prevSystemParams === nextSystemParams;
});

/**
 * Renders export status indicators for MOH, MO, and TAXONOMY systems
 * @param {Object} props - AG-Grid cell renderer props
 * @returns {JSX.Element} Status indicators for different export systems
 */
const StatusMediaRenderer = React.memo((props) => {
    return (
        <div className="v1-status-media-renderer">
            <div>{exportStatusHtml.getExportHTMLAgGrid(props?.data, 'MOH', props?.data?.exportStatusError)}</div>
            <div>{exportStatusHtml.getExportHTMLAgGrid(props?.data, 'MO', props?.data?.exportStatusError)}</div>
            <div>{exportStatusHtml.getExportHTMLAgGrid(props?.data, 'TAXONOMY', props?.data?.exportStatusError)}</div>
        </div>
    );
}, (prevProps, nextProps) => {
    return prevProps?.data?.export_status === nextProps?.data?.export_status &&
        prevProps?.data?.exportStatusError === nextProps?.data?.exportStatusError;
});



/**
 * CampaignTableAgGridV1 - A comprehensive AG-Grid based component for displaying and managing campaign data
 * 
 * This component provides functionality for:
 * - Displaying planned and unmapped campaigns in a tree-grid structure
 * - Filtering, searching, and pagination of campaign data
 * - Campaign actions (archive, restore, merge, navigation)
 * - Real-time data updates and scroll position management
 * - Integration with MediaOps API services
 *
 * @class CampaignTableAgGridV1
 * @extends {Component}
 */
class CampaignTableAgGridV1 extends Component {

    // Campaign Cell Renderer - removed memoization to allow immediate expansion icon updates
    CampaignCellRenderer = (props) => {
        // Handle loading row
        if (props.data?.isLoadingRow) {
            return <CustomLoadingCellRenderer  colDef={props.colDef}/>;
        }

        const handleExpandClick = (e) => {
            e.stopPropagation();
            const isCurrentlyExpanded = props.node.expanded;
            props.node.setExpanded(!isCurrentlyExpanded);

            // Force immediate refresh of this cell to update the icon
            setTimeout(() => {
                if (props.api) {
                    props.api.refreshCells({
                        rowNodes: [props.node],
                        force: true
                    });
                }
            }, 0);
        };

        const handleTextClick = (e) => {
            props.context.componentParent.navigateToPath(props.data, 'campaignDetails');
        };
        const gridDragOver = (event) => {
            const dragSupported = event.dataTransfer.types.length;

            if (dragSupported) {
            event.dataTransfer.dropEffect = "move";
            event.preventDefault();
            }
        };

        const gridDrop = (event) => {
            event.preventDefault();
            console.log("Drop event on ", event);
            const jsonData = event.dataTransfer.getData("application/json");
            const data = JSON.parse(jsonData);

            // if data missing or data has no it, do nothing
            if (!data || data.items.length === 0 || props.data.parent_id) {
            return;
            }
            mergeCampaigns(data);
        }
        /**
         * Handles merging of selected campaigns into the parent campaign.
         * Prepares the payload and calls the merge API.
         * Shows success or error messages based on the API response.
         */
        const mergeCampaigns = (data) => {
            this.props.setSpinnerCount(1);
            const selectedUnmapCampaigns = data?.items || [];
            const selectedUnmapCampaignsIndex = data?.indexs || [];
            const payload = {
                "parent_id": props.data?.synthetic_id,
                "amd_campaignid": props.data?.id,
                "campaign_name": props.data?.campaign_name,
                "mediaocean_id": props.data?.mediaocean_id,
                "export_status": props.data?.export_status,
                "campaign_taxonomy": props.data?.campaignTaxonomy,
                "campaign_taxonomy_display": props.data?.campaign_taxonomy_display,
                "taxonomy_schema": props.data?.taxonomy_schema,
                "updated_at": MediaOpsService.getTodaysDate(),
                "updated_by": this.props.omniProfile?.email,
                "client_code": props.data?.client_code,
                "start_date": props.data?.start_date.split(" ")[0],
                "end_date": props.data?.end_date.split(" ")[0]
            }

            if (!props.data?.synthetic_id) {
                payload["created_at"] = MediaOpsService.getTodaysDate();
                payload["created_by"] = this.props.omniProfile?.email;
            }

            const children = [];
            selectedUnmapCampaigns.forEach((campaign, index) => {
                const childObj = {};
                childObj['campaign_id'] = campaign?.synthetic_id;
                childObj['mediaocean_id'] = campaign?.mediaocean_id;
                childObj['client_code'] = campaign?.client_code;
                childObj['channels'] = campaign?.media_attributes?.channels?.map(channel => channel.channel_name).toString();
                childObj['start_date'] = campaign?.start_date.split("T")[0];
                childObj['end_date'] = campaign?.end_date.split("T")[0];
                children.push(childObj);
            });
            payload['children'] = children;
            let campaignId = encodeURIComponent(props.data?.id);
            MediaOpsService.mergeCampaigns(campaignId, payload).then((res) => {
                AlertMessagesService.showSuccessToastMessage(`${selectedUnmapCampaigns.length} campaign(s) successfully added to ${props.data?.campaignName}.`);
                let unMapCampaign = this.props.unMapCampaignObj.filter((item, index) => !selectedUnmapCampaignsIndex.includes(index));
                this.props.setUnMapCampaignObj(unMapCampaign);
                this.setState({ unMapCampaign: unMapCampaign,isCampaignMerged: true }, () => {
                    this.getCampaigns(true);
                });
                trackEvent('Campaigns are merged.', { access_from: 'Campaign Listing Page' });
            }).catch((error) => {
                console.log(error);
                AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => mergeCampaigns(data), title: 'Unable to add the campaigns', bodyMsgArr: [html`<p>${error?.response?.data?.error?.message}</p>`] }]);
                trackEvent('Campaigns are not merged.', { access_from: 'Campaign Listing Page', event: { error: error?.response?.data } });
            }).finally(() => {
                this.props.setSpinnerCount(0);
            });
        }

        const campaignName = props.data?.parent_id
            ? props.data?.campaignTaxonomy
            : props.data?.campaignDisplayName;

        // Check for children: either child_count > 0 OR nestedData exists
        const hasChildren = (props.data?.child_count > 0) ||
            (props.data?.nestedData && props.data.nestedData.length > 0);
        const level = props.node.level || 0;

        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                minHeight: '42px',
                paddingLeft: `${level * 20}px`,
            }}>
                {hasChildren ? (
                    <OmniIconComponent
                        className="is-size-3"
                        icon-id={props.node.expanded ? 'omni:interactive:down' : 'omni:interactive:right'}
                        onClick={handleExpandClick}
                        style={{ marginRight: '10px', cursor: 'pointer', flexShrink: 0, marginTop: '2px' }}
                    >
                    </OmniIconComponent>
                ) : (
                    <span style={{ width: '16px', marginRight: '8px', flexShrink: 0 }}></span>
                )}
                <span
                    onClick={handleTextClick}
                    onDragOver={gridDragOver}
                    onDrop={(e) => gridDrop(e)}                
                    style={{
                        cursor: 'pointer',
                        flex: 1,
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal'
                    }}
                >
                    {campaignName}
                </span>
            </div>
        );
    };

    /**
     * Renders the planned net media budget with proper currency formatting
     * @param {Object} props - AG-Grid cell renderer props
     * @param {Object} props.data - Row data containing campaign information
     * @returns {JSX.Element} Formatted currency display
     */
    maxNetMediaValue = 0;
    plannedNetMediaRenderer = (props) => {
        const isLocal = props.context.componentParent.props?.systemParametersData?.data?.isLocal;
        if(isLocal){
            this.maxNetMediaValue = Math.ceil(Math.max(this.maxNetMediaValue, Number(props?.data?.campaign_total_budget_local || 0)));
        } else {
            this.maxNetMediaValue = Math.ceil(Math.max(this.maxNetMediaValue, Number(props?.data?.campaign_total_budget || 0)));
        }
        return <PlannedNetMediaRenderer {...props} />;
    };

    /**
     * Renders export status indicators for MOH, MO, and TAXONOMY systems
     * @param {Object} props - AG-Grid cell renderer props
     * @param {Object} props.data - Row data containing export status information
     * @returns {JSX.Element} Status indicators for different export systems
     */
    statusMediaRenderer = (props) => {
        return <StatusMediaRenderer {...props} />;
    };

    /**
     * Renders action buttons for campaign operations (archive, restore, mapping, etc.)
     * @param {Object} props - AG-Grid cell renderer props
     * @param {Object} props.data - Row data containing campaign information
     * @param {Object} props.node - AG-Grid node information
     * @returns {JSX.Element} Action buttons based on campaign state and context
     */
    actionCellRenderer = (props) => {
        if (props.data?.isLoadingRow) return null;
        if (this.state.archiveTab) {
            return <button className="secondary medium" onClick={this.unArchiveCampaign.bind(this, props.data)}>Restore</button>
        } else {
            return (
                <div className="action-buttons" style={{ height: '37px' }}>
                    <ActionMenuRenderer
                        {...props}
                        id={props?.node?.id + "actions"}
                        isOpen={this.state.openActionMenuId === props?.data?.id}
                        onToggle={this.toggleActionMenu}
                        campaign={props?.data}
                        navigate={this.navigateToPath}
                        setSpinnerCount={this.props?.setSpinnerCount}
                        versionOneApi={this.props?.versionOneApi}
                        fetchCampaignData={this.getCampaigns}
                        onCopyPlacements={this.openCopyPlacementsDialog}
                    ></ActionMenuRenderer>

                    <MoreInfoRenderer
                        {...props}
                        id={props?.node?.id + "moreInfo"}
                        isOpen={this.state.openMoveDropdownId === props?.data?.id}
                        onToggle={this.toggleMoreInfo}
                        campaign={props?.data}
                    ></MoreInfoRenderer>
                </div>
            )
        }
    };

    /**
     * Applies custom cell renderers to column definitions recursively
     * @param {Array} cols - Array of column definitions
     * @returns {Array} Column definitions with applied renderers
     */
    applyRenderers = (cols) => {
        const cellRenderers = {
            netmedia: this.plannedNetMediaRenderer,
            status: this.statusMediaRenderer,
            actions: this.actionCellRenderer
        };

        // Wrapper to handle loading rows & custom renderers
        const wrappedCellRenderer = (props) => {
            if (props.data?.isLoadingRow) {
                return <CustomLoadingCellRenderer colDef={props.colDef} />;
            }

            if(cellRenderers[props.colDef.field]){
                return cellRenderers[props.colDef.field](props);
            } else {
                let displayValue = typeof props.colDef.valueFormatter === 'function'
                        ? props.colDef.valueFormatter(props)
                        : props.value;

                // Handle non-renderable values
                if (displayValue === null || displayValue === undefined) {
                    return null;
                }
                if (typeof displayValue === 'object') {
                    return null; // Don't render objects
                }

                return <span>{displayValue}</span>;
            }
        }
        
        // Prevent client-side sorting by returning 0 (preserve original order)
        const noSortComparator = () => 0;
        
        return cols.map(col => {
            // apply renderer and comparator to column
            return { 
                ...col, 
                cellRenderer: wrappedCellRenderer,
                comparator: noSortComparator
            };
        });
    };

    defaultState = {
        // Initialize state here if needed
        columnDefs: this.applyRenderers(CampaignTableAgGridV1ColumnDefs),
        autoGroupColumnDef: {
            headerName: "CAMPAIGN",
            field: "campaignDisplayName",
            wrapText: true,
            autoHeight: true,
            minWidth: 500,
            suppressHeaderMenuButton: true,
            cellRenderer: this.CampaignCellRenderer,
            floatingFilter: false,
            filter: false,
            suppressFloatingFilterButton: false,
            sortable: true,
            unSortIcon: true,
            comparator: () => 0, // Prevent client-side sorting, preserve original order
        },
        data: [{id: 'loading-row', isLoadingRow: true}],
        filterOptions: { campaignName: '', startDate: '', endDate: '', budgetMin: '', budgetMax: '', clientName: '' },
        hasNext: false,
        errorObject: { displayError: false },
        searchParam: '',
        filterApplied: false,
        sortColumnTitle: null,
        sortDirection: null,
        expandAllOnLoad: false,
        totalCount: 0,
        isTableNested: false,
        archiveTab: false,
        activeTab: false,
        mostRecentTab: true,
        campaigntabType:{'is-recent' : true},
        displayMergeModal: false,
        selectedCampaigns: [],
        openActionMenuId: null,
        openMoveDropdownId: null,
        campaignsFromAPI: [],
        copyPlacementsDialogOpen: false,
        selectedCampaignForCopy: null,
        spinnerCount: 0,
        // Unmapped campaigns fetched separately; used to show count in the warning bar
        unMapCampaign :this.props?.unMapCampaignObj || [],
        // Controls visibility of the warning/info bar above the grid
        showWarningBar: true,
        isMappingTable: false,
        tableWidth: '100%',
        panelWidth: '0',
        isLoadingMore: false, // Track pagination loading state
        isCampaignMerged: false,
    };
    sortObject = {
        'CAMPAIGN': 'campaign-name',
        'SOURCE': 'source-name',
        'START DATE': 'start-date',
        'END DATE': 'end-date',
        'MOH CLIENT CODE': 'client-code',
        'PLANIT CLIENT CODE': 'client-code-planit',
        'PRODUCT CODE': 'product-code-planit',
        'ESTIMATE': 'estimate-code-planit',
        'NET MEDIA': 'net-media',
        'MEDIAOCEAN ID': 'mediaocean-id',
        'STATUS': 'moh-status',
    };

    /**
     * Constructor - Initializes component state and creates grid reference
     * @param {Object} props - Component props from parent
     */
    constructor(props) {
        super(props);
        this.gridRef = React.createRef();
        this.state = this.defaultState;
        this.scrollTimeout = null;
        this.isClosingDropdowns = false;
        this.gridBody = null;
    }

    /**
     * Component lifecycle - Initializes data loading or restores previous state
     * Handles restoration of scroll position and table state when returning from navigation
     */
    componentDidMount() {
        const hasStoredState = this.props?.campaignTableData?.tableState && !this.props?.childCampaignIsCreated;
        this.props.setSpinnerCount(0);
        if (hasStoredState) {
            this.restoreTableState();
        } else {
            this.initializeFreshData();
        }
    }

    /**
     * Restores previously saved table state and scroll position
     * Called when user returns from navigation
     * @private
     */
    restoreTableState = () => {
        const { tableState, savedScrollTop, savedScrollLeft } = this.props.campaignTableData;

        // Cache scroll positions
        this.savedScrollTop = savedScrollTop;
        this.savedScrollLeft = savedScrollLeft;

        // Restore state with safe fallbacks
        this.setState({
            ...this.defaultState,
            data: tableState?.data || [],
            filterOptions: tableState?.filterOptions || this.defaultState.filterOptions,
            hasNext: tableState?.hasNext || false,
            errorObject: tableState?.errorObject || { displayError: false },
            searchParam: tableState?.searchParam || '',
            filterApplied: tableState?.filterApplied || false,
            expandAllOnLoad: tableState?.expandAllOnLoad || false,
            totalCount: tableState?.totalCount || 0,
            isTableNested: tableState?.isTableNested || false,
            archiveTab: tableState?.archiveTab || false,
            displayMergeModal: tableState?.displayMergeModal || false,
            selectedCampaigns: tableState?.selectedCampaigns || [],
            openActionMenuId: tableState?.openActionMenuId || null,
            openMoveDropdownId: tableState?.openMoveDropdownId || null,
            campaignsFromAPI: tableState?.campaignsFromAPI || [],
            spinnerCount: tableState?.spinnerCount || 0,
            activeTab: tableState?.activeTab || false,
            mostRecentTab: tableState?.mostRecentTab || false,
            campaigntabType:tableState?.campaigntabType || {'is-archive' : false},
            isCampaignMerged: tableState?.isCampaignMerged || false,
        }, () => {
            // Allow grid to render before restoring UI state
            setTimeout(() => {
                this.gridRef?.current?.api?.closeToolPanel();
                this.gridRef?.current?.api?.refreshCells({ force: true });
                this.restoreScrollPosition();
            }, 100);
        });
    };

    /**
     * Initializes component with fresh data from API
     * Called on first mount or after child campaign creation
     * @private
     */
    initializeFreshData = () => {
        this.getCampaigns();

        // Reset child campaign creation flag if applicable
        if (this.props?.setChildCampaignIsCreated) {
            this.props.setChildCampaignIsCreated(false);
        }
    };

    /**
     * Component lifecycle - Handles prop changes and grid updates
     * @param {Object} prevProps - Previous component props
     * @param {Object} prevState - Previous component state
     */
    componentDidUpdate(prevProps, prevState) {
        if (prevProps.versionOneApi !== this.props.versionOneApi) {
            this.getCampaigns(true);
        }

        window?.addEventListener('resize', this.refreshGrid);
    }

    /**
     * Component cleanup - Removes event listeners to prevent memory leaks
     */
    componentWillUnmount() {
        window?.removeEventListener('resize', this.refreshGrid);
        if (this.gridBody) {
            this.gridBody.removeEventListener('scroll', this.handleBodyScroll);
        }
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
        }
    }

    /**
     * Increments the local spinner count for loading state management
     */
    incrementSpinner = () => {
        this.setState(prevState => ({
            spinnerCount: prevState.spinnerCount + 1
        }));
    };

    /**
     * Decrements the local spinner count, ensuring it doesn't go below 0
     */
    decrementSpinner = () => {
        this.setState(prevState => ({
            spinnerCount: Math.max(0, prevState.spinnerCount - 1)
        }));
    };

    /**
     * Cancels the merge operation and closes the mapping modal
     */
    cancelMerge = () => {
        this.setState({ displayMergeModal: false })
    }

    /**
     * Resets component state to default and refreshes campaign data
     * Used after successful merge operations to update the view
     */
    resetStateAndUpdateCampaigns = () => {
        this.setState(this.defaultState, () => {
            this.props.setMappingTable(false);
            this.getCampaigns()
        })
    }

    /**
     * Restores an archived campaign to active status
     * @param {Object} campaignObj - Campaign object to restore
     * @param {string} campaignObj.id - Campaign identifier
     * @param {string} campaignObj.campaign_name - Campaign name for success message
     */
    unArchiveCampaign = (campaignObj) => {
        this.incrementSpinner();
        // Prepare payload for unarchiving, including plannedCampaign flag if it's a parent campaign
        let payload = { "isArchive": false, ...((campaignObj.parent_id == null || campaignObj.parent_id === '') ? { plannedCampaign: true } : {}) };
        let campaignId = encodeURIComponent(campaignObj.id);
        MediaOpsService.updateCampaign(payload, campaignId, this.props?.versionOneApi).then((response) => {
            AlertMessagesService.showSuccessToastMessage(`${campaignObj.campaign_name} has been restored successfully`);
            this.setState({ expandAllOnLoad: false },
                () => { this.getCampaigns(true); })
        }).catch(() => {
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.unArchiveCampaign(campaignObj), title: `Unable to restore the ${campaignObj.campaign_name}`, bodyMsgArr: [] }]);
        }).finally(() => {
            this.decrementSpinner();
        })
    }

    /**
     * Toggles the action menu dropdown for a specific row
     * Automatically closes any open more info dropdown to ensure only one dropdown is open at a time
     * @param {string} id - Campaign ID for the action menu
     */
    toggleActionMenu = (id) => {
        // If clicking the same menu, close it. Otherwise, open the new one and close any other dropdowns
        const newId = this.state.openActionMenuId === id ? null : id;

        // Close both dropdowns first, then set the new action menu (ensures only one dropdown open)
        this.setState({
            openActionMenuId: newId,
            openMoveDropdownId: null
        }, () => {
            // Force refresh of the actions column to update all dropdown states
            this.gridRef?.current?.api?.refreshCells({
                columns: ['actions'],
                force: true
            });
        });
    };

    openCopyPlacementsDialog = (campaign) => {
        this.setState({
            copyPlacementsDialogOpen: true,
            selectedCampaignForCopy: campaign,
            openActionMenuId: null,
        });
    };

    closeCopyPlacementsDialog = () => {
        this.setState({ copyPlacementsDialogOpen: false, selectedCampaignForCopy: null });
    };

    /**
     * Toggles the more info dropdown for a specific row
     * Automatically closes any open action menu to ensure only one dropdown is open at a time
     * @param {string} id - Campaign ID for the more info dropdown
     */
    toggleMoreInfo = (id) => {
        // If clicking the same dropdown, close it. Otherwise, open the new one and close any other dropdowns
        const newId = this.state.openMoveDropdownId === id ? null : id;

        // Close both dropdowns first, then set the new more info (ensures only one dropdown open)
        this.setState({
            openMoveDropdownId: newId,
            openActionMenuId: null
        }, () => {
            // Force refresh of the actions column to update all dropdown states
            this.gridRef?.current?.api?.refreshCells({
                columns: ['actions'],
                force: true
            });
        });
    };

    /**
     * Navigates to a specific component/page while preserving current table state
     * @param {Object} campaign - Campaign data object
     * @param {string} path - Target navigation path
     */
    navigateToPath = (campaign, path) => {
        this.setState({ openActionMenuId: null }, () => {
            this.saveScrollPosition();
            this.props.setCampaignTableData({ tableState: this.state, savedScrollTop: this.savedScrollTop, savedScrollLeft: this.savedScrollLeft });
            let parentCampaign;
            if (campaign?.parent_id) {
                parentCampaign = this.state.campaignsFromAPI.filter((item) => item.id === campaign.parent_id)[0];
            }
            Utils.navigateToComponent(campaign, path, this.props, {}, parentCampaign)
        });
    }

    /**
     * Restores the saved scroll position for both vertical and horizontal scrolling
     * Used when returning from navigation to maintain user's viewing position
     */
    restoreScrollPosition = () => {
        setTimeout(() => {
            const verticalViewport = document.querySelector('.ag-body-viewport');
            const horizontalViewport = document.querySelector('.ag-center-cols-viewport');

            if (verticalViewport && this.savedScrollTop != null) {
                verticalViewport.scrollTop = this.savedScrollTop;
            }
            if (horizontalViewport && this.savedScrollLeft != null) {
                horizontalViewport.scrollLeft = this.savedScrollLeft;
            }
        }, 50); // Slight delay to ensure DOM is ready
    };

    /**
     * Saves the current scroll position for both vertical and horizontal scrolling
     * Called before navigation to preserve user's viewing position
     */
    saveScrollPosition = () => {
        const verticalViewport = document.querySelector('.ag-body-viewport');
        const horizontalViewport = document.querySelector('.ag-center-cols-viewport');
        if (verticalViewport) {
            this.savedScrollTop = verticalViewport.scrollTop;
        }
        if (horizontalViewport) {
            this.savedScrollLeft = horizontalViewport.scrollLeft;
        }
    };

    /**
     * Processes campaign data from API response, handling both fresh data and pagination
     * @param {Object} response - API response containing campaign data
     * @param {boolean} isFreshData - Whether this is fresh data (true) or pagination (false)
     * @returns {Array} Processed campaign data array
     */
    processCampaignData = (response, isFreshData) => {
        if (isFreshData) {
            return MediaOpsService.prepareCampaignData(response?.data?.Items);
        } else {
            const nextPageItems = MediaOpsService.prepareCampaignData(response?.data?.Items);
            return [
                ...this.state.campaignsFromAPI,
                ...nextPageItems.filter(
                    (nextItem) => !this.state.campaignsFromAPI.some((existingItem) => existingItem.id === nextItem.id)
                ),
            ];
        }
    };

    /**
     * Fetches campaign data from the API with various filtering options
     * @param {boolean} isFreshData - Whether to fetch fresh data or append to existing
     * @param {string|null} campaignPmdsId - Parent campaign ID for child campaigns
     * @param {string|null} campaignSyntheticId - Specific campaign synthetic ID filter
     */
    getCampaigns = (isFreshData = true, campaignPmdsId = null, campaignSyntheticId = null) => {
        if (campaignPmdsId || campaignSyntheticId) {
            this.incrementSpinner();
        }
        // Set loading state for pagination
        this.setState({ isLoadingMore: true });
        let filterOptions = { ...this.state.filterOptions, source: 'planned', 'page-count': 10 };
        let hasNext = isFreshData ? '' : this.state.hasNext;

        if (campaignPmdsId) {
            filterOptions = { 'source': 'children', 'parent-id': campaignPmdsId };
            hasNext = null;
            if (campaignSyntheticId) {
                filterOptions['campaign-id'] = campaignSyntheticId;
            }
        }

        if (this.state.sortColumnTitle && this.state.sortDirection) {
            filterOptions['sort-by'] = this.sortObject[this.state.sortColumnTitle];
            filterOptions['sort-by-direction'] = this.state.sortDirection;
        }

        if (this.state.searchParam) {
            filterOptions['campaign-name'] = this.state.searchParam;
        }

        if (Object.keys(this.state.filterOptions).length > 1) {
            filterOptions = { ...filterOptions, ...this.state.filterOptions };
        }

        MediaOpsService.getCampaignData(filterOptions, hasNext, this.state.campaigntabType).then(response => {
            const campaignsFromAPI = this.processCampaignData(response, isFreshData);
            this.processTableResponse(response, isFreshData, campaignSyntheticId || campaignPmdsId, campaignsFromAPI);
            if (this.state.isCampaignMerged || !this.props.unMapCampaignObj?.length) {
                this.getUnMapCampaign();
            }else{
                let unMapCampaign = this.props.unMapCampaignObj;
                this.setState({ unMapCampaign:unMapCampaign });
            }
        }).catch((error) => {
            this.setState({ isLoadingMore: false });
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Refresh', retryBtnEvent: () => this.getCampaigns(isFreshData, campaignPmdsId), title: 'Campaign API cannot be loaded', bodyMsgArr: [] }]);
        }).finally(() => {
            if (campaignPmdsId || campaignSyntheticId) {
                this.decrementSpinner();
            }
        });
    }

    /**
     * Processes the API response and handles error states
     * @param {Object} response - API response object
     * @param {boolean} isFreshData - Whether this is fresh data or pagination
     * @param {string} parentId - Parent campaign ID for nested data
     * @param {Array} campaignsFromAPI - Processed campaign data from API
     */
    processTableResponse = (response, isFreshData, parentId, campaignsFromAPI) => {
        // Logic to process the table response and update state
        if (response?.data?.statuscode === 500) {
            const errorObj = JSON.parse(response?.data?.body);
            this.setState({ errorObject: { displayError: true } });
        }
        this.parseCampaignResponse(response, isFreshData, parentId, campaignsFromAPI);
    }

    /**
     * Parses campaign response data and prepares it for display in the grid
     * Handles both parent and child campaign data structures
     * @param {Object} response - Raw API response
     * @param {boolean} isFreshData - Whether this is fresh data or pagination
     * @param {string|null} parentId - Parent campaign ID for nested data
     * @param {Array} campaignsFromAPI - Array of campaigns from API
     */
    parseCampaignResponse = (response, isFreshData, parentId = null, campaignsFromAPI) => {
        let campaigns = [];
        let childCampaigns = [];
        const responseData = response['data']['Items'] ? response['data']['Items'] : [];
        const data = MediaOpsService.prepareCampaignData(responseData);
        const hasNext = response?.data?.paging?.next;
        
        // Prepare state updates to batch them together
        const stateUpdates = {};

        if (!parentId) {
            // Add placeholder nested data for campaigns with children
            data.forEach((campaign) => {
                if (campaign.child_count) {
                    campaign.nestedData = [{ campaignName: '', id: campaign.id + "child" }];
                }
            });

            // Determine expandAllOnLoad based on search/filter state
            stateUpdates.expandAllOnLoad = !!(this.state.searchParam || this.state.filterApplied || this.state.mostRecentTab);

            // Merge or replace campaigns based on fresh data flag
            if (!isFreshData) {
                // Filter out loading row before merging with new data
                const existingData = this.state.data.filter(item => !item.isLoadingRow);
                campaigns = [...existingData, ...data];
                stateUpdates.hasNext = hasNext;
            } else {
                campaigns = data;
                const totalRecord = this.state.campaigntabType['is-recent'] ? 10 : response['data']['total_records'];
                stateUpdates.totalCount = totalRecord;
                stateUpdates.hasNext = hasNext;
            }

            // Remove duplicates
            campaigns = campaigns.reduce((acc, current) => {
                const exist = acc.find(item => item.id === current.id);
                return exist ? acc : [...acc, current];
            }, []);
        } else {
            // Filter out loading row for child campaigns too
            campaigns = this.state.data.filter(item => !item.isLoadingRow);
            childCampaigns = data;
        }

        // Sorting logic for future use
        // let sortObj = { ...this.state.sort }
        // if (sortObj?.dir === 'asc') {
        //     sortObj.dir = 'desc';
        //     stateUpdates.sort = sortObj;
        // } else {
        //     sortObj.dir = 'asc';
        //     stateUpdates.sort = sortObj;
        // }

        // Apply batched state updates before creating nested data
        if (Object.keys(stateUpdates).length > 0) {
            this.setState(stateUpdates, () => {
                this.createNestedData(campaigns, isFreshData, childCampaigns, parentId, campaignsFromAPI);
            });
        } else {
            this.createNestedData(campaigns, isFreshData, childCampaigns, parentId, campaignsFromAPI);
        }
    }

    /**
     * Creates nested data structure for tree-grid display
     * Handles parent-child relationships and filtered data transformation
     * @param {Array} campaigns - Array of campaign data
     * @param {boolean} isFreshData - Whether this is fresh data or pagination
     * @param {Array} childCampaigns - Child campaign data for nested display
     * @param {string} parentId - Parent campaign ID for nested structure
     * @param {Array} campaignsFromAPI - Raw campaign data from API
     */
    createNestedData = (campaigns, isFreshData, childCampaigns, parentId, campaignsFromAPI) => {
        const setIsTableNested = (value) => this.setState({ isTableNested: value });
        let { transformedFilterData, fetchCount } = MediaOpsService.setCampaignsData(campaigns, this.state.isTableNested, setIsTableNested, childCampaigns, parentId, !!this.state.searchParam || this.state.filterApplied);

        if (!!this.state.searchParam || this.state.filterApplied || this.state.mostRecentTab) {
            transformedFilterData = transformedFilterData.map(item => {
                if (Array.isArray(item?.children) && item.children.length > 0) {
                    item.children = item.children.map(child => ({
                        ...child,
                        local_currency_name: item.local_currency_name,
                        parentSyntheticId: item?.synthetic_id,
                        parentStatus: item?.export_status
                    }));
                    item.nestedData = MediaOpsService.prepareCampaignData(item.children);
                } else {
                    item.nestedData = undefined;
                }
                return item;
            });
        }

        this.setState({ data: transformedFilterData, campaignsFromAPI }, () => {
            setTimeout(() => {
                this.refreshGrid(isFreshData);
                this.props.setLoadedCampaignData(transformedFilterData);
                // Clear loading state after grid is updated
                if (this.state.isLoadingMore) {
                    this.setState({ isLoadingMore: false });
                }
            });
        });
    }

    /**
     * Refreshes the AG-Grid and handles infinite scrolling logic
     * Manages spinner states and triggers additional data loading when needed
     */
    refreshGrid = (isFreshData = false) => {
        this.saveScrollPosition();
        setTimeout(() => {
            this.gridRef?.current?.api?.closeToolPanel();
            this.gridRef?.current?.api?.refreshCells({ force: true });

            // Expand all rows if search or filter is active, collapse if cleared
            this.gridRef?.current?.api?.forEachNode((node) => {
                if (node.data?.nestedData && node.data.nestedData.length > 0) {
                    if (this.state.searchParam || this.state.filterApplied) {
                        node.setExpanded(true);
                    } else if (isFreshData) {
                        node.setExpanded(false);
                    }
                }
            });

            this.restoreScrollPosition();
            const containerDiv = document.querySelector('.ag-center-cols-viewport');
            const contentDiv = document.querySelector('.ag-center-cols-container');

            if (!this.state.hasNext || this.state.campaigntabType['is-recent'] === true) {
                this.props.setSpinnerCount(0);
            } else if (containerDiv?.clientHeight !== contentDiv?.clientHeight && !this.state.isLoadingMore) {
                // Only trigger load if not already loading & concat data with loading row
                let dataWithLoader = this.state.data.concat([{id: 'loading-row', isLoadingRow: true}]);
                this.setState({ data: dataWithLoader }, () => {
                    this.getCampaigns(false);
                });
            } else {
                this.props.setSpinnerCount(0);
            }
        }, 100);
    };

    /**
     * Updates search results based on user input
     * @param {Object} event - Search event containing search value
     * @param {boolean} loadMoreData - Whether to load more paginated search results
     */
    updateSearch = (event, loadMoreData = false) => {
        const searchParam = event?.detail?.value ?? this.state.searchParam;
        this.setState({ searchParam: searchParam }, () => {
            if (searchParam) {
                this.setState({ data: [{id: 'loading-row', isLoadingRow: true}] });
                let queryParam;

                if (this.state.sortColumnTitle && this.state.sortDirection) {
                    queryParam = {
                        'campaign-name': searchParam,
                        'sort-by': this.sortObject[this.state.sortColumnTitle],
                        'sort-by-direction': this.state.sortDirection
                    };
                } else {
                    queryParam = { 'campaign-name': searchParam };
                }

                MediaOpsService.searchCampaign(queryParam, this.state.campaigntabType).then(result => {
                    if (searchParam && loadMoreData) {
                        this.parseCampaignResponse(result, false, null, this.state.campaignsFromAPI);
                    } else {
                        this.parseCampaignResponse(result, true, null, this.state.campaignsFromAPI);
                    }
                }).catch(error => {
                    this.setState({ data: [] });
                    AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.updateSearch(event, loadMoreData), title: `Unable to search the campaign, please refine your search text`, bodyMsgArr: [] }]);
                })
            } else {
                this.setState({ data: [{id: 'loading-row', isLoadingRow: true}] });
                this.getCampaigns(true);
            }
        })
    }

    /**
     * Fetches unmapped campaign data for merge operations
     * Sets unmapped campaigns in state and parent component
     */
    getUnMapCampaign = () => {
        let filterOptions = { 'source': 'unmapped' };
        MediaOpsService.getCampaignData(filterOptions, null, {'is-archive': false}).then(response => {
            let unMapCampaign = [];
            let responseData = response['data']['Items'] ? response['data']['Items'] : [];
            let data = MediaOpsService.prepareCampaignData(responseData);
            unMapCampaign = [...data];
            this.setState({ unMapCampaign,isCampaignMerged: false });
            this.props.setUnMapCampaignObj(unMapCampaign);
        }).catch((error) => {
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Refresh', retryBtnEvent: () => this.getUnMapCampaign(), title: 'Unmapped Campaign API cannot be loaded', bodyMsgArr: [] }]);
        });
    }

    /**
     * Handles row expansion events in the tree grid
     * Loads child campaign data when a parent row is expanded
     * @param {Object} event - AG-Grid row expansion event
     * @param {boolean} event.expanded - Whether the row was expanded or collapsed
     * @param {Object} event.node - The grid node that was expanded
     */
    onExpandClick = (event) => {
        if (!!this.state.searchParam || this.state.filterApplied || this.state.mostRecentTab) {
            return;
        }

        if (event?.expanded) {
            const node = event.node;
            if (!node.data.nested) {
                this.getCampaigns(false, node.data.id, node.data.campaign_id);
            }
        }
    };

    /**
     * AG-Grid initialization callback
     * Sets up grid API references and scroll event listeners for infinite scrolling
     * @param {Object} params - AG-Grid initialization parameters
     * @param {Object} params.api - Grid API reference
     * @param {Object} params.columnApi - Column API reference
     */
    onGridReady = (params) => {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;
        // Listen for scroll events on the body viewport
        this.gridBody = document.querySelector('.ag-body-viewport');

        if (this.gridBody) {
            this.gridBody.addEventListener('scroll', this.handleBodyScroll);
        }
        this.setState({ hasNext: this.state.hasNext });
    };

    /**
     * Handles scroll events for infinite scrolling functionality
     * Triggers data loading when user scrolls to the bottom of the grid
     * @param {Event} event - Scroll event object
     */
    handleBodyScroll = (event) => {
        const target = event.target;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;
        const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 50;

        // Close dropdowns immediately on first scroll, but don't repeat unnecessarily
        if (!this.isClosingDropdowns && (this.state.openActionMenuId || this.state.openMoveDropdownId)) {
            this.isClosingDropdowns = true;
            this.setState({
                openActionMenuId: null,
                openMoveDropdownId: null
            });
        }

        // Reset the flag after scrolling stops
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
        }
        this.scrollTimeout = setTimeout(() => {
            this.isClosingDropdowns = false;
        }, 150);

        if (scrolledToBottom && !this.state.isLoadingMore) {
            if (this.state?.totalCount === this.state?.campaignsFromAPI?.length || !this.state?.hasNext) {
                return;
            }
            let dataWithLoader = this.state.data.concat({ id: 'loading-row', isLoadingRow: true });
            this.setState({ data: dataWithLoader }, () => {
                this.getCampaigns(false); // You can trigger loading more data or any other action here
            });
        }
    };

    /**
     * Switches to the active campaigns tab
     * Resets state and reloads campaign data if currently on archive tab
     */
    setActiveTab = () => {
        if (this.state.archiveTab) {
            this.setState({ ...this.defaultState }, this.getCampaigns);
            this.setState({ isTableNested: false })
        }
    }

    /**
     * Switches to the archived campaigns tab
     * Resets state and loads archived campaign data if currently on active tab
     */
    setArchiveTab = () => {
        if (!this.state.archiveTab) {
            this.setState({ ...this.defaultState, archiveTab: true }, this.getCampaigns);
            this.setState({ isTableNested: false })
        }
    }

    setCampaignTab = (type) => {
        let archiveTab, activeTab, mostRecentTab = false;
        let tabType = this.state.campaigntabType;
        if(type === 'active'){
            activeTab = true
            tabType = {
                'is-archive' : false  
            }
        }else if(type === 'archive'){
            archiveTab = true;
            tabType = {
                'is-archive' : true  
            }
        }else{
            mostRecentTab = true;
            tabType = {
                'is-recent' : true  
            }
        }
        this.setState({ ...this.defaultState,activeTab: activeTab, archiveTab: archiveTab, mostRecentTab: mostRecentTab,campaigntabType:tabType }, this.getCampaigns);
        this.setState({ isTableNested: false }) 
    }
    /**
     * Applies filters to the campaign data or clears all filters
     * @param {Object} options - Filter options object containing various filter criteria
     * @param {boolean} clearAll - Whether to clear all filters (true) or apply them (false)
     */
    applyFilter = (options, clearAll = false) => {
        options = MediaOpsService.campaignFilterOptions(options);
        options['source'] = 'planned';
        this.setState({ filterOptions: options, filterApplied: !clearAll }, () => {
            if (clearAll) {
                this.setState({ data: [{ id: 'loading-row', isLoadingRow: true }] });
                this.getCampaigns(true);
            } else {
                this.setState({ data: [{ id: 'loading-row', isLoadingRow: true }] });
                if (this.state.sortColumnTitle && this.state.sortDirection) {
                    options['sort-by'] = this.sortObject[this.state.sortColumnTitle];
                    options['sort-by-direction'] = this.state.sortDirection;
                }

                MediaOpsService.getCampaignData(options, null, this.state.campaigntabType).then(result => {
                    this.parseCampaignResponse(result, true, null, this.state.campaignsFromAPI);
                    trackEvent('Campaigns Filter is applied', { access_from: 'Campaign Listing Page' });
                }).catch(error => {
                    this.setState({ data: [] });
                    AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.applyFilter(options, clearAll), title: `Unable to filter the campaign`, bodyMsgArr: [] }]);
                    trackEvent('Campaigns Filter has failed', { access_from: 'Campaign Listing Page', event: { error: error?.response?.data } });
                })
            }
        })
    }


    /**
     * Handles sort change events from the AG Grid table
     * @param {Object} event - The sort change event from AG Grid
     * @param {Object} event.api - The AG Grid API instance
     * @returns {void}
     * @description Extracts the current sort state from the grid, updates component state with the sort column title and direction,
     * and triggers a refresh of campaign data. If no columns are sorted, resets sort state and fetches campaigns with default sort.
     * Shows a loading row while data is being fetched.
     */
    onSortChanged = (event) => {
        const columnState = event.api.getColumnState();
        const sortedColumns = columnState.filter(col => col.sort != null);

        if (sortedColumns.length > 0) {
            const sortInfo = sortedColumns[0];
            const sortDirection = sortInfo.sort;
            // Get column definition to access the header name
            const columnDef = event.api.getColumnDef(sortInfo.colId);
            const sortColumnTitle = columnDef?.headerName || sortInfo.colId;

            this.setState({ sortColumnTitle, sortDirection, hasNext: null, data: [{ id: 'loading-row', isLoadingRow: true }] }, () => {
                this.getCampaigns(true);
            });
        } else {
            this.setState({ sortColumnTitle: null, sortDirection: null, hasNext: null, data: [{ id: 'loading-row', isLoadingRow: true }] }, () => {
                this.getCampaigns(true);
            });
        }
    };

    render() {

        // Set up the context for AG-Grid components
        const gridContext = { componentParent: this };
        const statusBar = {
            statusPanels: [
                {
                    statusPanel: "agAggregationComponent",
                    statusPanelParams: { aggFuncs: ["avg", "sum"] },
                },
                {
                    statusPanel: CustomStatusBar,
                    align: "left",
                    statusPanelParams: {
                        component: "campaigns",
                        totalCount: this.state.totalCount, // Pass total count dynamically
                    },
                },
            ],
        };

        return (
            <>
                {/* Top warning bar: shows count of unmapped Prisma files and CTA to map them */}
                {this.state?.unMapCampaign?.length > 0  && (
                        (() => {
                            const unmappedCount = (this.state.unMapCampaign && this.state.unMapCampaign.length) ? this.state.unMapCampaign.length : 0;
                            let message = `We couldn't map ${unmappedCount} Prisma files. Please map them to the right media plan for accurate reporting.`;
                            if(this.props.isMappingTable){
                                message = `Drag and drop the items from the right panel to the appropriate campaign.`;
                            }
                            return (
                                <div className={`campaign-text-bar ${this.props.isMappingTable ? 'info-bar':'warning-bar'}`} >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <OmniIconComponent icon-id={`omni:informative:${this.props.isMappingTable ? 'error':'alert'}`} className="is-size-4" />
                                        <div style={{ lineHeight: '1.1' }}>
                                            <div style={{ fontWeight: 600 }}>{message}</div>
                                        </div>
                                    </div>

                                    {!this.props.isMappingTable && <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <button className="secondary" onClick={() => this.props.setMappingTable(true)} style={{ minWidth: '120px' }}>Map Campaigns</button>
                                    </div>}
                                </div>
                            );
                        })()
                )}
                <div className='campaigns-table-container ag-theme-alpine' style={{ height: "calc(100vh - 175px)", padding: "10px 20px 70px 20px" }}>

                    {this.props.spinnerCount == 0 && <LoaderComponent spinnerCount={this.state.spinnerCount} displayWholePage={true} />}

                    <OmniToolbarComponent slot="header" className='toolBar'>
                        <div slot='start'>
                            <button className={`medium ${this.state.activeTab ? '' : 'tab'}`} onClick={()=>this.setCampaignTab('active')} disabled={this.props.isMappingTable}>Active Campaigns</button>
                            <button className={`medium ${this.state.archiveTab ? '' : 'tab'}`} onClick={()=>this.setCampaignTab('archive')} disabled={this.props.isMappingTable}>Archived Campaigns</button>
                            <button className={`medium activeButton ${this.state.mostRecentTab ? '' : 'tab'}`} onClick={()=>this.setCampaignTab('mostRecent')} disabled={this.props.isMappingTable}>Most Recent Campaigns</button>
                        </div>
                        {!this.state.mostRecentTab && <div slot="end" className="custom-dropdown-menu">
                            <OmniSearchComponent className="searchIcon search-input-icon" onSearchUpdate={this.updateSearch} slot="end" ph="Search"></OmniSearchComponent>
                            <div className="Line-Copy-2"></div>
                            <div className='filter-menu'>
                                <div className="separater-vertical"></div>
                                <FilterCampaign onApplyFilter={this.applyFilter.bind(this)} isTabRequired={false} isOldcampaignTable={true} maxValue={this.maxNetMediaValue} id={'campaign-table'}/>
                            </div>
                        </div>}
                    </OmniToolbarComponent>
                    
                    {this.state.errorObject.displayError ?
                        <div style={{ minHeight: "40vh" }}><NoDataComponent heading={"Error"} message={`Unable to pull campaigns. Something went wrong. Please refresh or contact your administrator. Error code 500.`} /></div>
                        :
                        <AgGridReact
                            key="campaigns-ag-grid-v1"
                            ref={this.gridRef}
                            columnDefs={this.state.columnDefs}
                            rowData={this.state.data}
                            autoGroupColumnDef={this.state.autoGroupColumnDef}
                            treeData={true}
                            treeDataChildrenField={"nestedData"}
                            getRowId={(params) => params?.data?.id?.toString()}
                            onRowGroupOpened={this.onExpandClick}
                            defaultColDef={defaultColDef}
                            sideBar={"columns"}
                            context={gridContext}
                            domLayout="normal"
                            onGridReady={this.onGridReady}
                            onSortChanged={this.onSortChanged}
                            suppressGroupRowsSticky={false}
                            statusBar={statusBar}
                            // Performance optimizations for large datasets (5k-10k rows)
                            suppressCellFocus={true}
                            suppressRowHoverHighlight={false}
                            rowBuffer={10}
                            debounceVerticalScrollbar={false}
                            suppressAnimationFrame={false}
                            asyncTransactionWaitMillis={50}
                            // Additional scroll performance optimizations
                            suppressScrollOnNewData={true}
                            animateRows={false}
                            suppressRowTransform={false}
                            // Critical fixes for blank rows with large datasets
                            maxBlocksInCache={10}
                            blockLoadDebounceMillis={50}
                            // Row height and rendering optimizations - disable animation for immediate height calculation
                            suppressMaxRenderedRowRestriction={true}
                            rowModelType="clientSide"
                            suppressRowVirtualisation={false}
                            groupDefaultExpanded={0}
                        />
                    }
                </div>

                <CopyPlacementsDialog
                    isOpen={this.state.copyPlacementsDialogOpen}
                    sourceCampaign={this.state.selectedCampaignForCopy}
                    onClose={this.closeCopyPlacementsDialog}
                    versionOneApi={this.props?.versionOneApi}
                    systemParametersData={this.props?.systemParametersData}
                />
            </>
        );
    }
}

export default CampaignTableAgGridV1;